/**
 * The photo folder on this computer, via the browser's File System Access API (Chrome/Edge desktop).
 * The folder handle is remembered in IndexedDB, so it's picked once; the browser may ask again for
 * permission after a restart, which needs a click.
 */

import { skuToBarcode } from '@/utils/skuBarcodeMapping';

type Perm = 'granted' | 'denied' | 'prompt';
export type DirHandle = FileSystemDirectoryHandle & {
  queryPermission?: (o: { mode: 'read' }) => Promise<Perm>;
  requestPermission?: (o: { mode: 'read' }) => Promise<Perm>;
  values: () => AsyncIterable<FileSystemHandle>;
};

const DB_NAME = 'fin-claire';
const STORE = 'handles';
const KEY = 'photoFolder';
const IMAGE_RE = /\.(jpe?g|png|webp)$/i;
const SKIPPED_RE = /\.(heic|heif|tiff?|raw|cr2|nef|arw|dng)$/i;

export const photoFolderSupported = () => typeof window !== 'undefined' && 'showDirectoryPicker' in window;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idb<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = run(db.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  });
}

/** Every computer keeps one folder per SKU, each named with its SKU, inside this folder. */
export const PHOTO_FOLDER_NAME = 'CCo. Item Photos';

/** A folder named like a SKU ("PM2027830", "PM 2027830") is one item's folder, not the main photo folder. */
export const looksLikeSkuFolder = (name: string) => /^[A-Z]{1,4}[\s_-]*\d{5,}$/i.test(name.trim());

export async function savedPhotoFolder(): Promise<DirHandle | null> {
  if (!photoFolderSupported()) return null;
  const handle = (await idb<DirHandle | undefined>('readonly', (s) => s.get(KEY)).catch(() => undefined)) || null;
  // An earlier pick of a single SKU's folder can never find the other SKUs; make them choose again.
  return handle && !looksLikeSkuFolder(handle.name) ? handle : null;
}

/** Opens the folder picker (must be called from a click). */
export async function choosePhotoFolder(): Promise<DirHandle> {
  const handle = (await (window as any).showDirectoryPicker({ id: 'fin-photo-folder', mode: 'read', startIn: 'desktop' })) as DirHandle;
  if (looksLikeSkuFolder(handle.name)) {
    throw new Error(`“${handle.name}” is one SKU’s folder. Choose “${PHOTO_FOLDER_NAME}”, the folder that holds all the SKU folders.`);
  }
  await idb('readwrite', (s) => s.put(handle, KEY));
  return handle;
}

const SEARCH_KEY = 'searchFolder';

/** Another folder Claire may search (Desktop, Documents…), remembered separately from the photo folder. */
export async function savedSearchFolder(): Promise<DirHandle | null> {
  if (!photoFolderSupported()) return null;
  return (await idb<DirHandle | undefined>('readonly', (s) => s.get(SEARCH_KEY)).catch(() => undefined)) || null;
}

export async function chooseSearchFolder(): Promise<DirHandle> {
  const handle = (await (window as any).showDirectoryPicker({ id: 'fin-search-folder', mode: 'read', startIn: 'documents' })) as DirHandle;
  await idb('readwrite', (s) => s.put(handle, SEARCH_KEY));
  return handle;
}

export async function folderPermission(handle: DirHandle, ask: boolean): Promise<Perm> {
  const current = (await handle.queryPermission?.({ mode: 'read' })) ?? 'granted';
  if (current === 'granted' || !ask) return current;
  return (await handle.requestPermission?.({ mode: 'read' })) ?? 'denied';
}

/**
 * Folder names vary ("PM2017047", "PM 2017047", "CT-PM2001003-01", "ORCA COIN PM2000924"), so a
 * folder matches when the SKU appears in its name as a whole code: the letters, optional space or
 * dash, then the digits, with no letter/digit right before it and no digit right after it.
 */
function skuPattern(sku: string): RegExp {
  const m = sku.toUpperCase().match(/^([A-Z]+)[\s-]*(\d+)$/);
  const core = m ? `${m[1]}[\\s_-]*${m[2]}` : sku.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Some photo folders are named with the item's barcode instead of its SKU.
  const barcode = skuToBarcode(sku);
  const alt = barcode && /^\d+$/.test(barcode) ? `|(^|\\D)${barcode}(?!\\d)` : '';
  return new RegExp(`(^|[^A-Z0-9])${core}(?!\\d)${alt}`, 'i');
}
const compact = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');

/** Lists of the photo folder's subfolders, kept briefly so many SKUs don't re-list thousands of folders. */
const listingCache = new WeakMap<DirHandle, { at: number; dirs: DirHandle[] }>();
const LISTING_TTL_MS = 5 * 60 * 1000;
const NESTED_SCAN_LIMIT = 200;

async function subfolders(dir: DirHandle): Promise<DirHandle[]> {
  const hit = listingCache.get(dir);
  if (hit && Date.now() - hit.at < LISTING_TTL_MS) return hit.dirs;
  const dirs: DirHandle[] = [];
  for await (const h of dir.values()) if (h.kind === 'directory') dirs.push(h as DirHandle);
  listingCache.set(dir, { at: Date.now(), dirs });
  return dirs;
}

function bestMatch(dirs: DirHandle[], sku: string): { dir: DirHandle | null; others: string[] } {
  const re = skuPattern(sku);
  const hits = dirs.filter((d) => re.test(d.name));
  if (!hits.length) return { dir: null, others: [] };
  hits.sort((a, b) => Number(compact(b.name) === compact(sku)) - Number(compact(a.name) === compact(sku)) || a.name.length - b.name.length);
  return { dir: hits[0], others: hits.slice(1).map((d) => d.name) };
}

/**
 * The SKU's folder: the chosen folder itself if it is the SKU folder, a subfolder whose name has the
 * SKU in it, or (only when the chosen folder holds a handful of folders, like date folders) one level down.
 */
async function findSkuFolder(root: DirHandle, sku: string): Promise<{ dir: DirHandle | null; others: string[] }> {
  if (skuPattern(sku).test(root.name)) return { dir: root, others: [] };
  const dirs = await subfolders(root);
  const direct = bestMatch(dirs, sku);
  if (direct.dir || dirs.length > NESTED_SCAN_LIMIT) return direct;
  for (const d of dirs) {
    const nested = bestMatch(await subfolders(d), sku);
    if (nested.dir) return nested;
  }
  return { dir: null, others: [] };
}

/**
 * Names of the folders inside the photo folder, freshly read. When it holds only a handful of folders
 * (like date folders), the folders one level down are listed too, as "parent/child".
 */
export async function listFolderNames(root: DirHandle): Promise<string[]> {
  listingCache.delete(root);
  const dirs = (await subfolders(root)).filter((d) => !d.name.startsWith('.'));
  const names = dirs.map((d) => d.name);
  if (dirs.length <= NESTED_SCAN_LIMIT) {
    for (const d of dirs) for (const n of await subfolders(d)) if (!n.name.startsWith('.')) names.push(`${d.name}/${n.name}`);
  }
  return names.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
}

export interface FileMatch {
  path: string;
  kind: 'file' | 'folder';
  size?: number;
  modified?: number;
}

const SEARCH_MAX_MATCHES = 500;
const SEARCH_MAX_ITEMS = 1_500_000;
const SEARCH_MAX_DEPTH = 12;
const SEARCH_SKIP_RE = /^(\.|\$|~\$|node_modules$)/i;
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Every word must be in the name (case-insensitive, * as a wildcard); "PM 2027830" also finds "PM2027830". */
function nameMatcher(query: string): (name: string) => boolean {
  const q = query.trim().toLowerCase();
  const words = q.split(/\s+/).filter(Boolean).map((w) => (w.includes('*') ? new RegExp(w.split('*').map(escapeRe).join('.*')) : w));
  const whole = compact(q);
  return (name) => {
    const n = name.toLowerCase();
    return words.every((w) => (typeof w === 'string' ? n.includes(w) : w.test(n))) || (whole.length >= 4 && compact(name).includes(whole));
  };
}

/** Searches every subfolder for file and folder names matching the query. Only names, sizes and dates leave the computer. */
export async function searchFolder(
  root: DirHandle,
  query: string,
  type: 'any' | 'files' | 'folders',
  onProgress?: (checked: number) => void,
): Promise<{ matches: FileMatch[]; scanned: number; stoppedAt: 'matches' | 'items' | null }> {
  const matches = nameMatcher(query);
  const wanted = (h: FileSystemHandle) => (type === 'files' ? h.kind === 'file' : type === 'folders' ? h.kind === 'directory' : true);
  const hits: { path: string; kind: FileMatch['kind']; file?: FileSystemFileHandle }[] = [];
  const stack: { dir: DirHandle; path: string; depth: number }[] = [{ dir: root, path: root.name, depth: 0 }];
  let scanned = 0;
  let stoppedAt: 'matches' | 'items' | null = null;
  walk: while (stack.length) {
    const { dir, path, depth } = stack.pop()!;
    try {
      for await (const h of dir.values()) {
        if (SEARCH_SKIP_RE.test(h.name)) continue;
        if (++scanned > SEARCH_MAX_ITEMS) {
          stoppedAt = 'items';
          break walk;
        }
        if (scanned % 2000 === 0) onProgress?.(scanned);
        const p = `${path}/${h.name}`;
        if (wanted(h) && matches(h.name)) {
          hits.push({ path: p, kind: h.kind === 'directory' ? 'folder' : 'file', file: h.kind === 'file' ? (h as FileSystemFileHandle) : undefined });
          if (hits.length >= SEARCH_MAX_MATCHES) {
            stoppedAt = 'matches';
            break walk;
          }
        }
        if (h.kind === 'directory' && depth < SEARCH_MAX_DEPTH) stack.push({ dir: h as DirHandle, path: p, depth: depth + 1 });
      }
    } catch {
      // A subfolder the browser won't open (system or locked folder); skip it.
    }
  }
  const out = await Promise.all(
    hits.map(async ({ path, kind, file }): Promise<FileMatch> => {
      if (!file) return { path, kind };
      try {
        const f = await file.getFile();
        return { path, kind, size: f.size, modified: f.lastModified };
      } catch {
        return { path, kind };
      }
    }),
  );
  out.sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true, sensitivity: 'base' }));
  return { matches: out, scanned: Math.min(scanned, SEARCH_MAX_ITEMS), stoppedAt };
}

/** Clears the remembered folder listing, e.g. after new SKU folders were added. */
export const refreshPhotoFolder = (root: DirHandle) => listingCache.delete(root);

export interface SkuPhotos {
  sku: string;
  found: boolean;
  folderName: string | null;
  /** Other folders whose names also have this SKU in them. */
  otherFolders: string[];
  files: File[];
  skipped: string[];
}

async function imagesIn(dir: DirHandle, into: File[], skipped: string[], depth: number) {
  for await (const h of dir.values()) {
    if (h.name.startsWith('.')) continue;
    if (h.kind === 'directory') {
      if (depth > 0) await imagesIn(h as DirHandle, into, skipped, depth - 1);
    } else if (IMAGE_RE.test(h.name)) into.push(await (h as FileSystemFileHandle).getFile());
    else if (SKIPPED_RE.test(h.name)) skipped.push(h.name);
  }
}

/** Photos in a SKU's folder (and one folder level inside it), in filename order (photo2 before photo10). */
export async function readSkuPhotos(root: DirHandle, sku: string, max = 24): Promise<SkuPhotos> {
  const { dir, others } = await findSkuFolder(root, sku);
  if (!dir) return { sku, found: false, folderName: null, otherFolders: [], files: [], skipped: [] };
  const files: File[] = [];
  const skipped: string[] = [];
  await imagesIn(dir, files, skipped, 1);
  files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
  return { sku, found: true, folderName: dir.name, otherFolders: others, files: files.slice(0, max), skipped: [...skipped, ...files.slice(max).map((f) => f.name)] };
}

/** Same size and quality the Product Entry page uses (2000px longest side, JPEG 85%). */
export function resizeForListing(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error(`Couldn't read ${file.name}`))), 'image/jpeg', 0.85);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Couldn't read ${file.name}`));
    };
    img.src = url;
  });
}
